// tslint:disable: variable-name

import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { IntegrationTokenPayloadInfo, PaymentModality } from '@core/models/swagger';
import { environment } from '@environments/environment';
import { getDate } from '@fwmovilidad/qb-home-ita-components-lib';
import {
  QbItaDigitalData,
  QbItaDigitalDataApplication,
  QbItaDigitalDataBrand,
  QbItaDigitalDataClient,
  QbItaDigitalDataCommProduct,
  QbItaDigitalDataEnvironment,
  QbItaDigitalDataErrorType,
  QbItaDigitalDataFlow,
  QbItaDigitalDataJourney,
  QbItaDigitalDataMortgage,
  QbItaDigitalDataPageType,
  QbItaDigitalDataProcessType,
  QbItaDigitalDataProduct,
  QbItaDigitalDataRiskType,
  QbItaDigitalDataUserStatus,
  QbItaDigitalDataUserType,
} from '@core/models/digitalData.model';
import { HomeRegimesEnum, HomeTypesEnum, PolicyActionEnum, VertiIntegrationFlow } from '@global/enums';
import { Flow } from '@global/routePaths';
import { select, Store } from '@ngrx/store';
import { AppState } from '@store/app.state';
import { getBackOfficeUser, getUserId } from '@store/flow-data/general';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import packagejson from '../../../../../package.json';

declare let digitalData: QbItaDigitalData;
declare let stepView: (stepName: string) => void;
declare let popupView: (popupName: string) => void;

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly destroy$: Subject<boolean> = new Subject<boolean>();
  private readonly store: Store<AppState>;
  private readonly router: Router;

  private readonly digitalData: QbItaDigitalData;

  stepView: (stepName: string) => void;
  popupView: (popupName: string) => void;

  constructor(private readonly injector: Injector) {
    this.store = this.injector.get(Store);
    this.router = this.injector.get(Router);

    this.initializeDigitalData();

    this.setEnvironment(environment.production ? 'production' : 'development');
    this.setAssetVersion(packagejson.version);

    this.store
      .pipe(select(getUserId))
      .pipe(takeUntil(this.destroy$))
      .subscribe((userId: string) => {
        this.setUserStatus(this.getUserStatusDigitalDataValue(userId));
      });

    this.store
      .pipe(select(getBackOfficeUser))
      .pipe(takeUntil(this.destroy$))
      .subscribe((backOffice: boolean) => {
        this.setUserType(this.getUserTypeDigitalDataValue(backOffice));
      });
  }

  sendStepView(stepName: string): void {
    this.setTimestamp(getDate().getTime().toString());
    this.setUrl(this.router.url);
    this.setReferrer(digitalData?.page_info?.url);
    this.setPrevPagename(digitalData?.custom?.pagename);
    this.setPagename(`${digitalData?.page_info?.country}:verti:qb:${stepName}`);
    this.setScreenName(`${digitalData?.page_info?.country}:verti:qb:${stepName}`);
    this.setTimestamp(getDate().getTime().toString());
    this.setStepVersion(`${digitalData?.process?.step_version ? Number(digitalData?.process?.step_version) + 1 : 0}`);
    if (environment.environmentName === 'DEV') {
      console.log(
        JSON.stringify(
          {
            method: 'stepView',
            stepName,
            digitalData: this.getDigitalData(),
          },
          null,
          2
        )
      );
    }
    try {
      stepView(this.encodeValue(stepName));
    } catch (e) {}
  }

  sendPopupView(popupName: string): void {
    if (environment.environmentName === 'DEV') {
      console.log(
        JSON.stringify(
          {
            method: 'popupView',
            popupName,
            digitalData: this.getDigitalData(),
          },
          null,
          2
        )
      );
    }
    try {
      popupView(this.encodeValue(popupName));
    } catch (e) {}
  }

  private initializeDigitalData(): void {
    digitalData = {
      environment: '',
      dl_version: '1.0',
      application: 'web',
      asset_version: '',
      page_category: {
        screen_name: '',
        section_1: '',
        section_2: '',
        section_3: '',
        section_4: '',
        section_5: '',
        section_6: '',
        section_7: '',
        section_8: '',
      },
      page_info: {
        brand: 'verti',
        page_type: '',
        country: 'it',
        channel: 'digital business',
        url: '',
        referrer: '',
        language: 'it',
        timestamp: '',
      },
      page_error: {
        error_type: '',
        error_message: '',
      },
      user: {
        user_status: '',
        user_type: 'client',
      },
      search: {
        search_keyword: '',
        search_result_number: '',
      },
      process: {
        process_type: 'quoting (multi-step)',
        full_operation_id: '',
        registration_operation_id: '',
        transaction_id: '',
        flow_id: '',
        step_number: '',
        step_description: '',
        step_common: '',
        step_basic: '',
        step_version: '',
        progress_id: '',
        purchase_price: '',
        modality: '',
        product_name: 'home',
      },
      custom: {
        zip_code: '',
        birth_date: '',
        gender: '',
        house_type: '',
        house_year: '',
        house_building_space: '',
        house_regime: '',
        house_use: '',
        house_coverage: '',
        current_company: '',
        start_date: '',
        viewed_coverages: '',
        viewed_price: '',
        payment_method: '',
        utm_source: '',
        utm_medium: '',
        utm_campaign: '',
        utm_term: '',
        utm_content: '',
        gclid: '',
        journey: '',
        flow: '',
        pagename: '',
        prevPagename: '',
        partnership: '',
        affinity: '',
        client: '',
        type_pack_shown: '',
        mortgage: '',
        risk_type: '',
        product: '',
        commProduct: '',
        policyNumber: '',
        quoteNumber: '',
        totalPremium: '',
        discount: '',
        client_user_id: '',
        payfreq: '',
      },
    };
  }

  private encodeValue(value?: string): string {
    if (!value && typeof value !== 'string') {
      return '';
    }
    return value
      .normalize('NFD')
      .replace(/([^n\u0300-\u036f]|n(?!\u0303(?![\u0300-\u036f])))[\u0300-\u036f]+/gi, '$1')
      .replace(/[^a-zA-Z 0-9.-|]+/g, '')
      .trim()
      .toLowerCase();
  }

  getDigitalData(): QbItaDigitalData {
    return digitalData;
  }

  setEnvironment(environment: QbItaDigitalDataEnvironment) {
    digitalData.environment = this.encodeValue(environment) as QbItaDigitalDataEnvironment;
  }

  setDlVersion(dl_version: string) {
    digitalData.dl_version = this.encodeValue(dl_version);
  }

  setApplication(application: QbItaDigitalDataApplication) {
    digitalData.application = this.encodeValue(application) as QbItaDigitalDataApplication;
  }

  setAssetVersion(asset_version: string) {
    digitalData.asset_version = this.encodeValue(asset_version);
  }

  setScreenName(screen_name: string) {
    digitalData.page_category.screen_name = this.encodeValue(screen_name);
  }

  setSection1(section_1: string) {
    digitalData.page_category.section_1 = this.encodeValue(section_1);
  }

  setSection2(section_2: string) {
    digitalData.page_category.section_2 = this.encodeValue(section_2);
  }

  setSection3(section_3: string) {
    digitalData.page_category.section_3 = this.encodeValue(section_3);
  }

  setSection4(section_4: string) {
    digitalData.page_category.section_4 = this.encodeValue(section_4);
  }

  setSection5(section_5: string) {
    digitalData.page_category.section_5 = this.encodeValue(section_5);
  }

  setSection6(section_6: string) {
    digitalData.page_category.section_6 = this.encodeValue(section_6);
  }

  setSection7(section_7: string) {
    digitalData.page_category.section_7 = this.encodeValue(section_7);
  }

  setsection8(section_8: string) {
    digitalData.page_category.section_8 = this.encodeValue(section_8);
  }

  setBrand(brand: QbItaDigitalDataBrand) {
    digitalData.page_info.brand = this.encodeValue(brand) as QbItaDigitalDataBrand;
  }

  setPageType(page_type: QbItaDigitalDataPageType) {
    digitalData.page_info.page_type = this.encodeValue(page_type) as QbItaDigitalDataPageType;
  }

  setCountry(country: string) {
    digitalData.page_info.country = this.encodeValue(country);
  }

  setChannel(channel: string) {
    digitalData.page_info.channel = this.encodeValue(channel);
  }

  setUrl(url: string) {
    digitalData.page_info.url = this.encodeValue(url);
  }

  setReferrer(referrer: string) {
    digitalData.page_info.referrer = this.encodeValue(referrer);
  }

  setLanguage(language: string) {
    digitalData.page_info.language = this.encodeValue(language);
  }

  setTimestamp(timestamp: string) {
    digitalData.page_info.timestamp = this.encodeValue(timestamp);
  }

  setErrorType(error_type: QbItaDigitalDataErrorType) {
    digitalData.page_error.error_type = this.encodeValue(error_type);
  }

  setErrorMessage(error_message: string) {
    digitalData.page_error.error_message = this.encodeValue(error_message);
  }

  setUserStatus(user_status: QbItaDigitalDataUserStatus) {
    digitalData.user.user_status = this.encodeValue(user_status) as QbItaDigitalDataUserStatus;
  }

  setUserType(user_type: QbItaDigitalDataUserType) {
    digitalData.user.user_type = this.encodeValue(user_type) as QbItaDigitalDataUserType;
  }

  setSearchKeyword(search_keyword: string) {
    digitalData.search.search_keyword = this.encodeValue(search_keyword);
  }

  setSearchResultNumber(search_result_number: string) {
    digitalData.search.search_result_number = this.encodeValue(search_result_number);
  }

  setProcessType(process_type: QbItaDigitalDataProcessType) {
    digitalData.process.process_type = this.encodeValue(process_type) as QbItaDigitalDataProcessType;
  }

  setFullOperationId(full_operation_id: string) {
    digitalData.process.full_operation_id = this.encodeValue(full_operation_id);
  }

  setRegistrationOperationId(registration_operation_id: string) {
    digitalData.process.registration_operation_id = this.encodeValue(registration_operation_id);
  }

  setTransactionId(transaction_id: string) {
    digitalData.process.transaction_id = this.encodeValue(transaction_id);
  }

  setFlowId(flow_id: string) {
    digitalData.process.flow_id = this.encodeValue(flow_id);
  }

  setStepNumber(step_number: string) {
    digitalData.process.step_number = this.encodeValue(step_number);
  }

  setStepDescription(step_description: string) {
    digitalData.process.step_description = this.encodeValue(step_description);
  }

  setStepCommon(step_common: string) {
    digitalData.process.step_common = this.encodeValue(step_common);
  }

  setStepBasic(step_basic: string) {
    digitalData.process.step_basic = this.encodeValue(step_basic);
  }

  setStepVersion(step_version: string) {
    digitalData.process.step_version = this.encodeValue(step_version);
  }

  setProgressId(progress_id: string) {
    digitalData.process.progress_id = this.encodeValue(progress_id);
  }

  setPurchasePrice(purchase_price: string) {
    digitalData.process.purchase_price = this.encodeValue(purchase_price);
  }

  setModality(modality: string) {
    digitalData.process.modality = this.encodeValue(modality);
  }

  setProductName(product_name: string) {
    digitalData.process.product_name = this.encodeValue(product_name);
  }

  setZipCode(zip_code: string) {
    digitalData.custom.zip_code = this.encodeValue(zip_code);
  }

  setBirthDate(birth_date: string) {
    digitalData.custom.birth_date = this.encodeValue(birth_date);
  }

  setGender(gender: string) {
    digitalData.custom.gender = this.encodeValue(gender);
  }

  setHouseType(house_type: string) {
    digitalData.custom.house_type = this.encodeValue(house_type);
  }

  setHouseYear(house_year: string) {
    digitalData.custom.house_year = this.encodeValue(house_year);
  }

  setHouseBuildingSpace(house_building_space: string) {
    digitalData.custom.house_building_space = this.encodeValue(house_building_space);
  }

  setHouseRegime(house_regime: string) {
    digitalData.custom.house_regime = this.encodeValue(house_regime);
  }

  setHouseUse(house_use: string) {
    digitalData.custom.house_use = this.encodeValue(house_use);
  }

  setHouseCoverage(house_coverage: string) {
    digitalData.custom.house_coverage = this.encodeValue(house_coverage);
  }

  setCurrentCompany(current_company: string) {
    digitalData.custom.current_company = this.encodeValue(current_company);
  }

  setStartDate(start_date: string) {
    digitalData.custom.start_date = this.encodeValue(start_date);
  }

  setViewedCoverages(viewed_coverages: string) {
    digitalData.custom.viewed_coverages = this.encodeValue(viewed_coverages);
  }

  setViewedPrice(viewed_price: string) {
    digitalData.custom.viewed_price = this.encodeValue(viewed_price);
  }

  setPaymentMethod(payment_method: string) {
    digitalData.custom.payment_method = this.encodeValue(payment_method);
  }

  setUtmSource(utm_source: string) {
    digitalData.custom.utm_source = this.encodeValue(utm_source);
  }

  setUtmMedium(utm_medium: string) {
    digitalData.custom.utm_medium = this.encodeValue(utm_medium);
  }

  setUtmCampaign(utm_campaign: string) {
    digitalData.custom.utm_campaign = this.encodeValue(utm_campaign);
  }

  setUtmTerm(utm_term: string) {
    digitalData.custom.utm_term = this.encodeValue(utm_term);
  }

  setUtmContent(utm_content: string) {
    digitalData.custom.utm_content = this.encodeValue(utm_content);
  }

  setGclid(gclid: string) {
    digitalData.custom.gclid = this.encodeValue(gclid);
  }

  setJourney(journey: QbItaDigitalDataJourney) {
    digitalData.custom.journey = this.encodeValue(journey) as QbItaDigitalDataJourney;
  }

  setFlow(flow: QbItaDigitalDataFlow) {
    digitalData.custom.flow = this.encodeValue(flow) as QbItaDigitalDataFlow;
  }

  setPagename(pagename: string) {
    digitalData.custom.pagename = this.encodeValue(pagename);
  }

  setPrevPagename(prevPagename: string) {
    digitalData.custom.prevPagename = this.encodeValue(prevPagename);
  }

  setPartnership(partnership: string) {
    digitalData.custom.partnership = this.encodeValue(partnership);
  }

  setAffinity(affinity: string) {
    digitalData.custom.affinity = this.encodeValue(affinity);
  }

  setClient(client: QbItaDigitalDataClient) {
    digitalData.custom.client = this.encodeValue(client) as QbItaDigitalDataClient;
  }

  setTypePackShown(type_pack_shown: string) {
    digitalData.custom.type_pack_shown = this.encodeValue(type_pack_shown);
  }

  setMortgage(mortgage: QbItaDigitalDataMortgage) {
    digitalData.custom.mortgage = this.encodeValue(mortgage) as QbItaDigitalDataMortgage;
  }

  setRiskType(risk_type: QbItaDigitalDataRiskType) {
    digitalData.custom.risk_type = this.encodeValue(risk_type) as QbItaDigitalDataRiskType;
  }

  setProduct(product: QbItaDigitalDataProduct) {
    digitalData.custom.product = this.encodeValue(product) as QbItaDigitalDataProduct;
  }

  setCommProduct(commProduct: QbItaDigitalDataCommProduct) {
    digitalData.custom.commProduct = this.encodeValue(commProduct) as QbItaDigitalDataCommProduct;
  }

  setPolicyNumber(policyNumber: string) {
    digitalData.custom.policyNumber = this.encodeValue(policyNumber);
  }

  setQuoteNumber(quoteNumber: string) {
    digitalData.custom.quoteNumber = this.encodeValue(quoteNumber);
  }

  setTotalPremium(totalPremium: string) {
    digitalData.custom.totalPremium = this.encodeValue(totalPremium);
  }

  setDiscount(discount: string) {
    digitalData.custom.discount = this.encodeValue(discount);
  }

  setClientUserId(client_user_id: string) {
    digitalData.custom.client_user_id = this.encodeValue(client_user_id);
  }

  setProductPayFrequency(payfreq: string) {
    digitalData.custom.payfreq = this.encodeValue(payfreq);
  }

  getProductPayFrequency(paymentFrequency: string) {
    switch (paymentFrequency) {
      case PaymentModality.PaymentModalityTypeCodeEnum.Everyyear:
        return '1';
      case PaymentModality.PaymentModalityTypeCodeEnum.Everysixmonths:
        return '2';
      case PaymentModality.PaymentModalityTypeCodeEnum.Quarterly:
        return '4';
      case PaymentModality.PaymentModalityTypeCodeEnum.Monthly:
        return '12';
      default:
        return '';
    }
  }

  getHomeRegimeDigitalDataValue(homeRegime: string) {
    switch (homeRegime) {
      case HomeRegimesEnum?.OWNER:
        return 'own';
      case HomeRegimesEnum?.TENANT:
        return 'rent';
      default:
        return '';
    }
  }

  getCommProductDigitalDataValue(homeRegime: string) {
    switch (homeRegime) {
      case HomeRegimesEnum?.OWNER:
        return 'casa_propieta';
      case HomeRegimesEnum?.TENANT:
        return 'casa_affitto';
      default:
        return '';
    }
  }

  getHomeTypeDigitalDataValue(homeRegimeCode: string) {
    switch (homeRegimeCode) {
      case HomeTypesEnum?.FLAT:
        return 'appartamento';
      case HomeTypesEnum?.TERRACED:
        return 'villa schiera';
      case HomeTypesEnum?.VILLA:
        return 'villa singola';
      default:
        return '';
    }
  }

  getUserStatusDigitalDataValue(userId: string) {
    return userId ? 'logged' : 'not logged';
  }

  getUserTypeDigitalDataValue(backOffice: boolean) {
    switch (backOffice) {
      case true:
        return 'agent';
      case false:
        return 'client';
      default:
        return 'client';
    }
  }

  getBooleanDigitalDataValue(value: boolean) {
    switch (value) {
      case true:
        return 'yes';
      case false:
        return 'no';
      default:
        return '';
    }
  }

  getJourneyDigitalDataValue(flow: Flow) {
    switch (flow) {
      case Flow.NEW_QUOTATION:
        return 'nb';
      case Flow.MODIFY_QUOTATION:
        return 'rn';
      case Flow.MODIFY_POLICY:
        return 'am';
      case Flow.MODIFY_RENEWAL:
        return 'rn';
      default:
        return '';
    }
  }

  getJourneyDigitalDataValueByVertiFlow(vertiIntegration: VertiIntegrationFlow) {
    switch (vertiIntegration) {
      case VertiIntegrationFlow.NewQuotationNotLogged:
      case VertiIntegrationFlow.NewQuotationLogged:
        return 'nb';
      case VertiIntegrationFlow.ModifyQuotationData:
      case VertiIntegrationFlow.ModifyQuotationCoverages:
        return 'rn';
      case VertiIntegrationFlow.ModifyPolicy:
      case VertiIntegrationFlow.CancelPolicy:
        return 'am';
      case VertiIntegrationFlow.CancelRenewal:
      case VertiIntegrationFlow.ShortPolicyRenewal:
      case VertiIntegrationFlow.CompletePolicyRenewal:
        return 'rn';
      default:
        return '';
    }
  }

  getFlowTypeDigitalDataValue(userId: string, flow: Flow, policyAction: PolicyActionEnum) {
    switch (flow) {
      case Flow.NEW_QUOTATION:
        return userId ? 'uq' : 'nq';
      case Flow.MODIFY_QUOTATION:
        return 'cq';
      case Flow.MODIFY_POLICY:
        switch (policyAction) {
          case PolicyActionEnum.CANCEL:
            return 'can';
          case PolicyActionEnum.EDIT:
            return 'cha';
          case PolicyActionEnum.RENEW:
            return 'dis';
          default:
            return 'cha';
        }
      case Flow.MODIFY_RENEWAL:
        return 'cq';
      default:
        return '';
    }
  }

  getStepBasicDigitalData(
    stepName: string,
    flow: Flow,
    modifyIntegrationAction: IntegrationTokenPayloadInfo.ActionEnum
  ) {
    switch (stepName) {
      case 'home_cap_page':
        return 'p1:quoting start';
      case 'home_quote_calculation':
        return 'p2:price view';
      case 'home_quote_calculation_coverages':
        return flow === Flow.MODIFY_RENEWAL && modifyIntegrationAction === 'complete_renewal' ? '' : 'p2:price view';
      case 'home_ph_address':
        return flow === Flow.MODIFY_RENEWAL && modifyIntegrationAction === 'short_renewal' ? '' : 'p3:purchasing start';
      case 'home_personal_data':
        return 'p3:purchasing start';
      default:
        return '';
    }
  }
}
